import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import RightSidebarContainer from "../components/RightSidebarContainer";
import Confetti from "react-confetti";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import Lightbulb from "@mui/icons-material/Lightbulb";
import CollapsibleSidebarSection from "../components/CollapsibleSidebarSection";
// import Grid from '@mui/material/Grid2';
import ThreadCard from "../components/ThreadCard";
import {
  fetchTopics,
  fetchTopContributors,
  fetchInstitutions,
  default as api,
} from "../services/api";
import { useInstitution } from "../components/InstitutionContext";
import TopicsSidebar from "../components/TopicsSidebar";
import TopContributorSidebar from "../components/TopContributorSidebar";
import { useTheme } from "@mui/material/styles";
import InfiniteScroll from "react-infinite-scroll-component";
import { useMediaQuery } from "@mui/material";
import ThreadSearchBar from "../components/ThreadSearchBar";
import ThreadFilterTabs from "../components/ThreadFilterTabs";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import getUserDisplayName from "../utils/getUserDisplayName";
import FilledButton from "../components/FilledButton";
import OutlinedButton from "../components/OutlinedButton";
import IconOnlyChip from "../components/IconOnlyChip";
import Tutorial from "../components/Tutorial";
import LoginPromptAlert from "../components/LoginPromptAlert";
import InstitutionPromptAlert from "../components/InstitutionPromptAlert";
import VerifyEmailPromptAlert from "../components/VerifyEmailPromptAlert";
import AddPostDialog from "../components/AddPostDialog";

// Function to strip HTML tags from content
const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

export default function CommunityPage() {
  const [topics, setTopics] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [contributors, setContributors] = useState([]);
  const theme = useTheme(); // Get current theme mode
  const { selectedInstitution } = useInstitution();
  const [visibleCount, setVisibleCount] = useState(10);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, emailVerified, setUser } = useAuth();
  const [institutionName, setInstitutionName] = useState("");
  const verifiedUser = user && emailVerified;
  const unverifiedUser = user && !emailVerified;

  const sidebarContainerRef = useRef(null);
  const [sidebarFits, setSidebarFits] = useState(false);
  const topOffset = 64; // height of the navigation bar

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [sortBy, setSortBy] = useState("newest");

  // Topic and subtopic filters were removed, leaving only search and tab filters
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("recent");
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourDisabled, setTourDisabled] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [tourShown, setTourShown] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const addPostRef = useRef(null);
  const searchSortRef = useRef(null);
  const myPostsRef = useRef(null);

  const tourSteps = [
    {
      selector: null,
      title: "Welcome to the Community Page",
      content:
        "Follow this step-by-step tutorial to learn how to use the community page effectively.",
      position: "center",
    },
    {
      selector: addPostRef,
      title: "View and manage your posts",
      content: "See all your posts and comments in one place.",
    },
    {
      selector: searchSortRef,
      title: "Search or sort posts",
      content: "Can search for keyword, sort by date or other metrics",
    },
    {
      selector: myPostsRef,
      title: "View and manage your posts",
      content: "See all your posts and comments in one place.",
    },
    {
      selector: null,
      title: "Ready to Explore?",
      content: "Finish tutorial and start exploring.",
      final: true,
      position: "center",
    },
  ];

  const advanceTutorial = () =>
    setTourStep((prev) => Math.min(prev + 1, tourSteps.length - 1));

  const handleTutorialAction = (stepIndex, action) => () => {
    if (tourOpen && tourStep === stepIndex) {
      advanceTutorial();
    } else if (action) {
      action();
    }
  };

  const handlePostAdded = (thread, isFirst) => {
    const msg = isFirst
      ? "🎉 Congratulations on starting your first discussion thread! Post submitted"
      : "Post submitted";
    setSuccessMessage(msg);
    if (isFirst) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 6000);
    }
  };

  useEffect(() => {
    const nameFromUser = user?.userprofile?.institution?.display_name;
    if (nameFromUser) {
      setInstitutionName(nameFromUser);
      return;
    }
    if (!selectedInstitution) {
      setInstitutionName("");
      return;
    }
    const loadInst = async () => {
      try {
        const list = await fetchInstitutions();
        const inst = list.find(
          (i) => String(i.id) === String(selectedInstitution),
        );
        if (inst) setInstitutionName(inst.display_name);
      } catch (err) {
        console.error("Failed to load institution:", err);
        setErrorMessage("Failed to load institution.");
      }
    };
    loadInst();
  }, [user, selectedInstitution]);

  let filteredThreads = [...threads];

  if (searchQuery.trim() !== "") {
    const q = searchQuery.toLowerCase();
    filteredThreads = filteredThreads.filter(
      (thread) =>
        thread.title.toLowerCase().includes(q) ||
        stripHtml(thread.content).toLowerCase().includes(q),
    );
  }

  let tabFilteredThreads = [...filteredThreads];
  if (activeTab === "unanswered") {
    tabFilteredThreads = tabFilteredThreads.filter(
      (t) => (t.repliesCount || 0) === 0,
    );
  } else if (activeTab === "pinned") {
    tabFilteredThreads = tabFilteredThreads.filter((t) => t.is_pinned);
  } else if (activeTab === "solved") {
    tabFilteredThreads = tabFilteredThreads.filter((t) => t.is_solved);
  } else if (activeTab === "unsolved") {
    tabFilteredThreads = tabFilteredThreads.filter((t) => !t.is_solved);
  }

  if (sortBy === "newest") {
    tabFilteredThreads.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  } else if (sortBy === "oldest") {
    tabFilteredThreads.sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );
  } else if (sortBy === "most_viewed") {
    tabFilteredThreads.sort(
      (a, b) => (b.view_count || 0) - (a.view_count || 0),
    );
  } else if (sortBy === "most_commented") {
    tabFilteredThreads.sort(
      (a, b) => (b.repliesCount || 0) - (a.repliesCount || 0),
    );
  }

  // Topic-specific subtopics were removed

  const fetchMoreThreads = () => {
    setTimeout(() => {
      setVisibleCount((prev) => prev + 10);
    }, 500);
  };

  // Topic selection handler removed

  const hasMore = visibleCount < tabFilteredThreads.length;

  const loadAll = useCallback(async () => {
    setPageLoaded(false);
    if (!selectedInstitution) {
      setTopics([]);
      setThreads([]);
      setContributors([]);
      setPageLoaded(true);
      return;
    }
    setLoadingData(true);
    try {
      const [topicData, contributorData] = await Promise.all([
        fetchTopics(selectedInstitution),
        fetchTopContributors(selectedInstitution),
      ]);

      if (!Array.isArray(topicData)) {
        console.error("Invalid API response format:", topicData);
        setErrorMessage("Failed to fetch community data.");
      } else {
        const allThreads = topicData.flatMap((topic) =>
          (topic.threads || []).map((thread) => ({
            ...thread,
            topicId: topic.id,
            topicName: topic.name,
            topicColor: topic.color || "#9c27b0",
            subtopicId: thread.subtopic_id || "",
            subtopicName: thread.subtopic || "",
          })),
        );

        allThreads.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );

        const topicStats = topicData.map((topic) => ({
          ...topic,
          totalThreads: topic.threads ? topic.threads.length : 0,
        }));

        setTopics(topicStats);
        setThreads(allThreads);
      }

      setContributors(contributorData);
    } catch (err) {
      console.error("Failed to fetch community data", err);
      setErrorMessage("Failed to fetch community data.");
    } finally {
      setLoadingData(false);
      setPageLoaded(true);
    }
  }, [selectedInstitution]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const updateFit = () => {
      if (sidebarContainerRef.current) {
        const fits =
          sidebarContainerRef.current.offsetHeight <=
          window.innerHeight - topOffset;
        setSidebarFits(fits);
      }
    };
    updateFit();
    window.addEventListener("resize", updateFit);
    return () => window.removeEventListener("resize", updateFit);
  }, [topics, contributors, isSmallScreen]);

  useEffect(() => {
    if (
      verifiedUser &&
      user?.userprofile &&
      user.userprofile.has_visited_community_page === false &&
      pageLoaded
    ) {
      setTourOpen(true);
    }
  }, [verifiedUser, user, pageLoaded, isSmallScreen]);

  useEffect(() => {
    if (tourOpen) setTourShown(true);
  }, [tourOpen]);

  useEffect(() => {
    if (
      verifiedUser &&
      !tourOpen &&
      tourShown &&
      user?.userprofile &&
      user.userprofile.has_visited_community_page === false
    ) {
      api
        .put("/users/profile/", {
          profile: { has_visited_community_page: true },
        })
        .then(() => {
          setUser((prev) =>
            prev
              ? {
                  ...prev,
                  userprofile: {
                    ...prev.userprofile,
                    has_visited_community_page: true,
                  },
                }
              : prev,
          );
        })
        .catch((err) => {
          console.error(err);
          setErrorMessage("Failed to update profile.");
        });
    }
  }, [tourOpen, tourShown, user, setUser]);

  return (
    <>
      <Box
        sx={{
          maxWidth: "1200px",
          width: "100%", // ✅ Forces full width within parent
          mx: "auto",
          py: 2,
          minHeight: "80vh",
          justifyContent: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          {/* Threads panel with scroll */}
          <Box sx={{ flexGrow: 1, px: 4, py: 4 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <ForumRoundedIcon sx={{ color: theme.palette.success.main }} />
              Community Discussions
              {verifiedUser && (
                <Tooltip title="Show tutorial">
                  <IconOnlyChip
                    icon={<Lightbulb fontSize="small" />}
                    color="success"
                    onClick={() => setTourOpen(true)}
                    sx={{ p: 0.5 }}
                  />
                </Tooltip>
              )}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Explore the latest topics and engage in discussions about courses,
              premed resources, and more.
            </Typography>

            {!selectedInstitution && (
              <InstitutionPromptAlert
                message="to explore community discussions."
                sx={{ mt: 1, mb: 2 }}
              />
            )}

            {institutionName && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  mb: 2,
                }}
              >
                <Typography variant="body1" sx={{ fontStyle: "italic" }}>
                  {institutionName}
                </Typography>
              </Box>
            )}

            {isSmallScreen && (
              <>
                <CollapsibleSidebarSection
                  title="Topics"
                  icon={<LocalOfferIcon fontSize="small" color="success" />}
                >
                  <TopicsSidebar
                    topics={topics}
                    institutionId={selectedInstitution}
                  />
                </CollapsibleSidebarSection>
                <CollapsibleSidebarSection
                  title="Top Contributors"
                  icon={<EmojiEventsIcon fontSize="small" color="success" />}
                >
                  <TopContributorSidebar contributors={contributors} />
                </CollapsibleSidebarSection>
              </>
            )}

            {isSmallScreen && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {verifiedUser && (
                  <OutlinedButton
                    ref={myPostsRef}
                    component={RouterLink}
                    to={
                      tourOpen && tourStep === 3
                        ? undefined
                        : `/community/user/${encodeURIComponent(
                            getUserDisplayName(user),
                          )}`
                    }
                    onClick={handleTutorialAction(3, () =>
                      navigate(
                        `/community/user/${encodeURIComponent(getUserDisplayName(user))}`,
                      ),
                    )}
                    text="View My Posts"
                    color="success"
                    size="large"
                    sx={{ mb: 1, flexShrink: 0, whiteSpace: "nowrap" }}
                  />
                )}
                {verifiedUser && (
                  <FilledButton
                    ref={addPostRef}
                    onClick={handleTutorialAction(1, () => setPostOpen(true))}
                    text="Add a Post"
                    color="success"
                    size="large"
                    sx={{ mb: 1, flexShrink: 0, whiteSpace: "nowrap" }}
                    data-tour-id="community-add-post"
                  />
                )}
                {!user && (
                  <LoginPromptAlert
                    from={location.pathname}
                    message="to Add a Post."
                    sx={{ mb: 2 }}
                  />
                )}
                {unverifiedUser && (
                  <VerifyEmailPromptAlert
                    message="to Add a Post."
                    sx={{ mb: 2 }}
                  />
                )}
              </Box>
            )}

            {isSmallScreen ? (
              <>
                <Box
                  ref={searchSortRef}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                    mb: 2,
                  }}
                >
                  <ThreadSearchBar
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ flexGrow: 1 }}
                  />
                  <FormControl size="small">
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <MenuItem value="newest">Newest</MenuItem>
                      <MenuItem value="oldest">Oldest</MenuItem>
                      <MenuItem value="most_viewed">Most Viewed</MenuItem>
                      <MenuItem value="most_commented">Most Commented</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <ThreadFilterTabs value={activeTab} onChange={setActiveTab} />
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  mb: 3,
                  gap: 2,
                }}
              >
                <ThreadFilterTabs value={activeTab} onChange={setActiveTab} />
                <Box
                  ref={searchSortRef}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    ml: "auto",
                  }}
                >
                  <ThreadSearchBar
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <FormControl size="small">
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <MenuItem value="newest">Newest</MenuItem>
                      <MenuItem value="oldest">Oldest</MenuItem>
                      <MenuItem value="most_viewed">Most Viewed</MenuItem>
                      <MenuItem value="most_commented">Most Commented</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}
            <InfiniteScroll
              dataLength={Math.min(visibleCount, tabFilteredThreads.length)}
              next={fetchMoreThreads}
              hasMore={hasMore}
              loader={
                <Typography variant="body2" sx={{ my: 2 }}>
                  Loading...
                </Typography>
              }
            >
              {tabFilteredThreads.slice(0, visibleCount).map((thread) => (
                <ThreadCard
                  key={thread.id}
                  thread={thread}
                  disableVoting={loadingData}
                />
              ))}
            </InfiniteScroll>
          </Box>
          {!isSmallScreen && (
            <RightSidebarContainer
              ref={sidebarContainerRef}
              sticky={sidebarFits}
              topOffset={topOffset}
              sx={{ ml: 3, mt: 6 }}
            >
              {verifiedUser && (
                <OutlinedButton
                  ref={myPostsRef}
                  component={RouterLink}
                  to={
                    tourOpen && tourStep === 3
                      ? undefined
                      : `/community/user/${encodeURIComponent(
                          getUserDisplayName(user),
                        )}`
                  }
                  onClick={handleTutorialAction(3, () =>
                    navigate(
                      `/community/user/${encodeURIComponent(getUserDisplayName(user))}`,
                    ),
                  )}
                  text="View My Posts"
                  color="success"
                  size="large"
                  sx={{ width: "100%", mb: 1 }}
                />
              )}
              {verifiedUser && (
                <FilledButton
                  ref={addPostRef}
                  onClick={handleTutorialAction(1, () => setPostOpen(true))}
                  text="Add a Post"
                  color="success"
                  size="large"
                  sx={{ width: "100%", mb: 1 }}
                  data-tour-id="community-add-post"
                />
              )}
              {!user && (
                <LoginPromptAlert
                  from={location.pathname}
                  message="to Add a Post."
                  sx={{ width: "100%", mb: 2 }}
                />
              )}
              {unverifiedUser && (
                <VerifyEmailPromptAlert
                  message="to Add a Post."
                  sx={{ width: "100%", mb: 2 }}
                />
              )}
              <TopicsSidebar
                topics={topics}
                institutionId={selectedInstitution}
              />
              <TopContributorSidebar contributors={contributors} />
            </RightSidebarContainer>
          )}
        </Box>
      </Box>
      {tourOpen && (
        <Tutorial
          steps={tourSteps}
          isOpen={tourOpen}
          setIsOpen={setTourOpen}
          currentStep={tourStep}
          setCurrentStep={setTourStep}
          disabledActions={tourDisabled}
          setDisabledActions={setTourDisabled}
        />
      )}
      <AddPostDialog
        open={postOpen}
        onClose={() => setPostOpen(false)}
        previousPath={location.pathname}
        onAdded={handlePostAdded}
        onSuccess={() => {}}
        onError={(msg) => setErrorMessage(msg)}
      />
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMessage("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      {showConfetti && (
        <Confetti width={window.innerWidth} height={window.innerHeight} />
      )}
    </>
  );
}
